import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import { PriceCard } from "@/components/PriceCard";
import { DoctolibLogo } from "@/components/DoctolibLogo";
import useSeo from "@/hooks/useSeo";
import { SITE } from "@/constants/site";
import useI18n from "@/i18n";
import { pagePath } from "@/i18n/config";

export default function TarifsPage() {
    const { t, lang } = useI18n();
    const page = t.pages.tarifs;

    useSeo({
        title: t.seo.tarifs.title,
        description: t.seo.tarifs.description,
        path: pagePath(lang, "tarifs"),
        lang,
        pageKey: "tarifs",
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Header />
            <main className="pt-24" data-testid="page-tarifs">
                <div className="mx-auto max-w-[1080px] px-5 pb-20 pt-6">
                    <PageBreadcrumb label={t.nav.tarifs} pageKey="tarifs" />

                    <Reveal>
                        <p className="eyebrow mt-8">{page.eyebrow}</p>
                        <h1 className="title-grad mt-4 text-4xl sm:text-5xl">{page.h1}</h1>
                        <hr className="rule-gold mt-5 w-24" />
                        <p className="mt-5 max-w-[62ch] text-lg text-ink2">{page.lead}</p>
                    </Reveal>

                    <Reveal delay={0.05}>
                        <h2 className="mt-14 text-2xl text-terrat md:text-3xl">
                            {page.consultationsTitle}
                        </h2>
                        <hr className="rule-gold mt-3 w-12" />
                        <div className="mt-6 grid gap-5 sm:grid-cols-2">
                            {t.tarifsData.consultations.map(([label, prix, detail], i) => (
                                <div
                                    key={label}
                                    className="card card-motif flex items-center justify-between gap-5 p-6"
                                    data-testid={`tarif-page-${i + 1}`}
                                >
                                    <div>
                                        <h3 className="text-lg font-bold text-olivet">{label}</h3>
                                        <p className="mt-1 text-sm text-ink2">{detail}</p>
                                    </div>
                                    <p className="font-display text-3xl text-terrat">{prix}</p>
                                </div>
                            ))}
                        </div>
                        <p className="mt-4 text-sm text-ink2">{page.consultationsNote}</p>
                    </Reveal>

                    <Reveal>
                        <h2 className="mt-14 text-2xl text-terrat md:text-3xl">{page.soinsTitle}</h2>
                        <hr className="rule-gold mt-3 w-12" />
                    </Reveal>
                    <div className="mt-6 grid gap-x-6 md:grid-cols-2">
                        <div>
                            {t.tarifsData.soinsA.map(([titre, lignes]) => (
                                <PriceCard key={titre} titre={titre} lignes={lignes} />
                            ))}
                        </div>
                        <div>
                            {t.tarifsData.soinsB.map(([titre, lignes]) => (
                                <PriceCard key={titre} titre={titre} lignes={lignes} />
                            ))}
                        </div>
                    </div>

                    <Reveal>
                        <h2 className="mt-10 text-2xl text-terrat md:text-3xl">{page.laserTitle}</h2>
                        <hr className="rule-gold mt-3 w-12" />
                        <p className="mb-5 mt-4 max-w-[62ch] text-sm text-ink2">{page.laserNote}</p>
                    </Reveal>
                    {t.tarifsData.laser.map(([titre, lignes]) => (
                        <PriceCard key={titre} titre={titre} lignes={lignes} split />
                    ))}

                    <Reveal>
                        <div className="card mt-8 flex flex-col items-start justify-between gap-5 p-6 md:flex-row md:items-center">
                            <p className="max-w-[58ch] text-sm text-ink2">{page.note}</p>
                            <a
                                href={SITE.doctolibUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="tarifs-page-rdv-button"
                                className="btn btn-primary shrink-0"
                            >
                                {t.common.rdvOn}
                                <DoctolibLogo height={15} color="#FFFFFF" />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </main>
            <Footer />
        </>
    );
}
